
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";


import {
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    BookOpen,
    CalendarDays,
    Check,
    ChevronRight,
    Clock3,
    Compass,
    Hotel,
    Link2,
    MapPin,
    Share2,
} from "lucide-react";

import {
    FaFacebookF,
    FaXTwitter,
    FaLinkedinIn,
    FaWhatsapp,
} from "react-icons/fa6";



import blogImg from "../assests/images/blog-bg.jpg";

/* ============================================================
   CONFIGURATION
============================================================ */

const API_URL = "https://dubaibudgetivehotels.com/api";
const SITE_URL = "https://dubaibudgetivehotels.com";

const HOTEL_IMAGE =
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=85";

/* ============================================================
   HELPERS
============================================================ */

function getImage(image, fallback = blogImg) {
    if (!image || typeof image !== "string") {
        return fallback;
    }

    if (/^(https?:\/\/|data:)/i.test(image)) {
        return image;
    }

    if (image.startsWith("//")) {
        return `https:${image}`;
    }

    if (image.startsWith("/uploads/")) {
        return `${SITE_URL}${image}`;
    }

    return `${SITE_URL}/uploads/blogs/${image.replace(/^\/+/, "")}`;
}

function imageFallback(event) {
    event.currentTarget.onerror = null;
    event.currentTarget.src = blogImg;
}

function formatDate(date, short = false) {
    if (!date) return "";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleDateString("en-US", {
        month: short ? "short" : "long",
        day: "numeric",
        year: "numeric",
    });
}

function plainText(value) {
    return String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim();
}

function getBlogResponse(response) {
    const data = response?.data;
    return data?.blog || data?.data?.blog || data?.data || data;
}

function getBlogListResponse(response) {
    const data = response?.data;
    const value = data?.blogs ?? data?.data ?? data;

    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;

    return [];
}

/* ============================================================
   SMALL ARTICLE CARD
============================================================ */

function RelatedCard({ blog }) {
    return (
        <Link
            to={`/blogs/${blog.slug}`}
            className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-[#E2ECE6] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#B8DFC6] hover:shadow-[0_18px_45px_rgba(6,78,59,0.10)]"
        >
            <div className="relative h-[230px] overflow-hidden bg-[#E8F0EB]">
                <img
                    src={getImage(blog.thumbnail_image || blog.image)}
                    alt={blog.title || "Dubai travel article"}
                    loading="lazy"
                    onError={imageFallback}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <span className="absolute left-5 top-5 rounded-full bg-white/95 px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider text-[#168B43]">
                    {blog.category?.name || "Travel Guide"}
                </span>

                <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#168B43] shadow-md transition group-hover:bg-[#168B43] group-hover:text-white">
                    <ArrowUpRight size={18} />
                </span>
            </div>

            <div className="flex flex-1 flex-col p-6">
                {blog.created_at && (
                    <p className="flex items-center gap-2 text-[11px] font-semibold text-[#8492A6]">
                        <CalendarDays size={14} className="text-[#168B43]" />
                        {formatDate(blog.created_at, true)}
                    </p>
                )}

                <h3 className="mt-3 line-clamp-2 text-[21px] font-extrabold leading-snug tracking-tight text-[#17324D] transition group-hover:text-[#168B43]">
                    {blog.title}
                </h3>

                <div className="mt-auto flex items-center justify-between border-t border-[#E8F0EB] pt-5 text-[12px] font-extrabold text-[#168B43]">
                    Read Article
                    <ArrowRight
                        size={17}
                        className="transition group-hover:translate-x-1"
                    />
                </div>
            </div>
        </Link>
    );
}

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function BlogDetail() {
    const { slug } = useParams();

    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    /* ==========================================================
       FETCH ARTICLE AND LATEST BLOGS
    ========================================================== */

    useEffect(() => {
        let active = true;

        async function fetchData() {
            setLoading(true);
            setBlog(null);
            setError("");

            const [blogResult, latestResult] = await Promise.allSettled([
                axios.get(`${API_URL}/blogs/${slug}`),
                axios.get(`${API_URL}/blogs`),
            ]);

            if (!active) return;

            if (blogResult.status === "fulfilled") {
                const data = getBlogResponse(blogResult.value);

                if (data && !Array.isArray(data) && data.title) {
                    setBlog(data);
                } else {
                    setError("This article could not be found.");
                }
            } else {
                console.error("Failed to load blog:", blogResult.reason);
                setError("This article could not be loaded.");
            }

            if (latestResult.status === "fulfilled") {
                setLatestBlogs(getBlogListResponse(latestResult.value));
            } else {
                setLatestBlogs([]);
            }

            setLoading(false);
        }

        fetchData();

        return () => {
            active = false;
        };
    }, [slug]);

    /* ==========================================================
       DERIVED DATA
    ========================================================== */

    const currentUrl =
        typeof window !== "undefined"
            ? window.location.href
            : `${SITE_URL}/blogs/${slug}`;

    const canonicalUrl = `${SITE_URL}/blogs/${blog?.slug || slug}`;

    const bannerImage = blog
        ? getImage(blog.banner_image || blog.image)
        : blogImg;

    const readingTime = useMemo(() => {
        const content = plainText(blog?.content);

        if (!content) return "1 min read";

        const words = content.split(/\s+/).length;

        return `${Math.max(1, Math.ceil(words / 200))} min read`;
    }, [blog?.content]);

    const latestOtherBlogs = useMemo(() => {
        return latestBlogs
            .filter((item) => item.slug !== blog?.slug)
            .slice(0, 5);
    }, [latestBlogs, blog?.slug]);

    const relatedBlogs = useMemo(() => {
        if (!blog) return [];

        return latestBlogs
            .filter((item) => item.slug !== blog.slug)
            .filter(
                (item) =>
                    !blog.category?.id ||
                    String(item.category?.id) === String(blog.category.id)
            )
            .slice(0, 3);
    }, [latestBlogs, blog]);

    /* ==========================================================
       SHARE FUNCTIONS
    ========================================================== */

    const copyLink = async () => {
        try {
            if (
                typeof navigator !== "undefined" &&
                navigator.clipboard &&
                window.isSecureContext
            ) {
                await navigator.clipboard.writeText(currentUrl);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = currentUrl;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";

                document.body.appendChild(textArea);
                textArea.select();

                const success = document.execCommand("copy");
                document.body.removeChild(textArea);

                if (!success) throw new Error("Copy failed");
            }

            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Unable to copy link:", err);
        }
    };

    const shareButtons = [
        {
            name: "WhatsApp",
            icon: FaWhatsapp,
            href: `https://wa.me/?text=${encodeURIComponent(
                `${blog?.title || ""} ${currentUrl}`
            )}`,
        },
        {
            name: "Facebook",
            icon: FaFacebookF,
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                currentUrl
            )}`,
        },
        {
            name: "X",
            icon: FaXTwitter,
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                currentUrl
            )}&text=${encodeURIComponent(blog?.title || "")}`,
        },
        {
            name: "LinkedIn",
            icon: FaLinkedinIn,
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                currentUrl
            )}`,
        },
    ];

    /* ==========================================================
       LOADING STATE
    ========================================================== */

    if (loading) {
        return (
            <div className="flex min-h-[80vh] items-center justify-center bg-[#F5FAF7] px-5">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-[3px] border-[#DCEAE1] border-t-[#168B43]" />

                    <p className="mt-5 text-sm font-semibold text-[#718096]">
                        Loading your travel story...
                    </p>
                </div>
            </div>
        );
    }

    /* ==========================================================
       ERROR / NOT FOUND
    ========================================================== */

    if (!blog) {
        return (
            <div className="flex min-h-[80vh] items-center justify-center bg-[#F5FAF7] px-5">
                <div className="max-w-lg text-center">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                        <Compass size={36} />
                    </div>

                    <h1 className="mt-6 text-[34px] font-extrabold text-[#17324D]">
                        Story Not Found
                    </h1>

                    <p className="mt-4 text-sm leading-8 text-[#718096]">
                        {error ||
                            "We couldn't find this article. Explore our travel journal for more Dubai inspiration."}
                    </p>

                    <Link
                        to="/blogs"
                        className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white hover:bg-[#087D37]"
                    >
                        <ArrowLeft size={17} />
                        Back to Blog
                    </Link>
                </div>
            </div>
        );
    }

    const description = plainText(
        blog.meta_description || blog.excerpt || blog.title
    );

    return (
        <>
            {/* =====================================================
          SEO
      ===================================================== */}

            <Helmet>
                <title>
                    {blog.meta_title || `${blog.title} | Dubai Budgetive Hotels`}
                </title>

                <meta name="description" content={description} />

                <link rel="canonical" href={canonicalUrl} />

                <meta
                    property="og:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    property="og:description"
                    content={description}
                />

                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={bannerImage} />

                <meta name="twitter:card" content="summary_large_image" />

                <meta
                    name="twitter:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    name="twitter:description"
                    content={description}
                />

                <meta name="twitter:image" content={bannerImage} />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="min-h-screen overflow-hidden bg-white text-[#17324D]">

                {/* =====================================================
            FULL-WIDTH BLOG BANNER
        ===================================================== */}

                <section className="relative isolate flex min-h-[470px] items-center overflow-hidden bg-[#064E3B] pt-24 sm:min-h-[540px]">
                    <img
                        src={bannerImage}
                        alt={blog.title}
                        onError={imageFallback}
                        className="absolute inset-0 -z-20 h-full w-full object-cover"
                    />

                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/85 to-[#064E3B]/55" />

                    <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#032F28]/40 to-transparent" />

                    <div className="mx-auto w-full max-w-[1536px] px-4 py-16 sm:px-6 lg:px-8">
                        <div className="max-w-[900px]">

                            {/* BREADCRUMB */}
                            <nav
                                aria-label="Breadcrumb"
                                className="mb-8 flex flex-wrap items-center gap-2 text-xs font-semibold text-white/75"
                            >
                                <Link to="/" className="hover:text-white">
                                    Home
                                </Link>

                                <ChevronRight size={14} />

                                <Link to="/blogs" className="hover:text-white">
                                    Travel Blog
                                </Link>

                                <ChevronRight size={14} />

                                <span className="max-w-[260px] truncate text-[#B6F2CB]">
                                    {blog.title}
                                </span>
                            </nav>

                            {/* CATEGORY */}
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB] backdrop-blur-sm">
                                <BookOpen size={15} />
                                {blog.category?.name || "Dubai Travel Guide"}
                            </div>

                            {/* TITLE */}
                            <h1 className="max-w-[900px] text-[37px] font-extrabold leading-[1.14] tracking-[-0.045em] text-white sm:text-[53px] lg:text-[65px]">
                                {blog.title}
                            </h1>

                            {/* EXCERPT */}
                            {blog.excerpt && (
                                <p className="mt-6 max-w-[720px] text-[15px] leading-8 text-white/80 sm:text-base">
                                    {plainText(blog.excerpt)}
                                </p>
                            )}

                            {/* META */}
                            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs font-semibold text-white/80">
                                {blog.created_at && (
                                    <span className="inline-flex items-center gap-2">
                                        <CalendarDays size={16} className="text-[#B6F2CB]" />
                                        {formatDate(blog.created_at)}
                                    </span>
                                )}

                                <span className="inline-flex items-center gap-2">
                                    <Clock3 size={16} className="text-[#B6F2CB]" />
                                    {readingTime}
                                </span>

                                <span className="inline-flex items-center gap-2">
                                    <MapPin size={16} className="text-[#B6F2CB]" />
                                    Dubai Travel Journal
                                </span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =====================================================
            MAIN ARTICLE + SIDEBAR
        ===================================================== */}

                <section className="bg-[#F5FAF7] py-14 sm:py-20">
                    <div className="mx-auto grid w-full max-w-[1536px] items-start gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-12 lg:px-8">

                        {/* ARTICLE CONTENT */}
                        <article className="min-w-0 overflow-hidden rounded-[22px] border border-[#E2ECE6] bg-white p-6 sm:p-9 lg:p-12">

                            {/* INTRO */}
                            <div className="border-b border-[#E3ECE6] pb-8">
                                <div className="mb-5 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                                    <span className="h-[2px] w-8 bg-[#168B43]" />
                                    Inside The Story
                                </div>

                                <p className="border-l-[3px] border-[#168B43] pl-5 text-[17px] font-semibold leading-8 text-[#425466] sm:text-[19px]">
                                    {plainText(blog.excerpt) ||
                                        "Explore helpful travel advice and inspiration for your next Dubai getaway."}
                                </p>
                            </div>

                            {/* BLOG HTML CONTENT */}
                            <div
                                className="
                  blog-content
                  prose prose-slate
                  mt-9 max-w-none
                  prose-headings:font-extrabold
                  prose-headings:tracking-tight
                  prose-headings:text-[#17324D]
                  prose-h2:mt-10
                  prose-h2:text-[29px]
                  prose-h3:mt-8
                  prose-h3:text-[23px]
                  prose-p:text-[15px]
                  prose-p:leading-[1.95]
                  prose-p:text-[#536779]
                  prose-a:font-bold
                  prose-a:text-[#168B43]
                  prose-strong:text-[#17324D]
                  prose-li:leading-8
                  prose-li:text-[#536779]
                  prose-img:rounded-xl
                  prose-img:shadow-sm
                  prose-blockquote:rounded-r-xl
                  prose-blockquote:border-l-[#168B43]
                  prose-blockquote:bg-[#F5FAF7]
                  prose-blockquote:px-6
                  prose-blockquote:py-4
                "
                                dangerouslySetInnerHTML={{
                                    __html: blog.content || "",
                                }}
                            />

                            {/* ARTICLE FOOTER */}
                            <div className="mt-12 flex flex-wrap items-center justify-between gap-5 border-t border-[#E3ECE6] pt-7">
                                <Link
                                    to="/blogs"
                                    className="inline-flex items-center gap-3 text-sm font-extrabold text-[#17324D] transition hover:text-[#168B43]"
                                >
                                    <ArrowLeft size={17} className="text-[#168B43]" />
                                    Back to Travel Blog
                                </Link>

                                <span className="text-xs font-bold text-[#8492A6]">
                                    Dubai Budgetive Hotels
                                </span>
                            </div>

                            {/* =================================================
                  SOCIAL SHARING
              ================================================= */}

                            <div className="mt-10 rounded-[18px] border border-[#DCEAE1] bg-[#F5FAF7] p-6">
                                <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
                                    <div>
                                        <h3 className="flex items-center gap-2 text-[17px] font-extrabold text-[#17324D]">
                                            <Share2 size={19} className="text-[#168B43]" />
                                            Share This Article
                                        </h3>

                                        <p className="mt-2 text-xs leading-6 text-[#718096]">
                                            Share this Dubai travel guide with friends and family.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        {shareButtons.map((item) => {
                                            const Icon = item.icon;

                                            return (
                                                <a
                                                    key={item.name}
                                                    href={item.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Share on ${item.name}`}
                                                    title={`Share on ${item.name}`}
                                                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#DCEAE1] bg-white text-[#17324D] transition hover:border-[#168B43] hover:bg-[#168B43] hover:text-white"
                                                >
                                                    <Icon size={18} />
                                                </a>
                                            );
                                        })}

                                        <button
                                            type="button"
                                            onClick={copyLink}
                                            className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#168B43] px-4 text-xs font-extrabold text-white transition hover:bg-[#087D37]"
                                        >
                                            {copied ? (
                                                <Check size={16} />
                                            ) : (
                                                <Link2 size={16} />
                                            )}

                                            {copied ? "Copied!" : "Copy Link"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </article>

                        {/* =================================================
                SIDEBAR
            ================================================= */}

                        <aside className="space-y-6 lg:sticky lg:top-28">

                            {/* SIDEBAR INTRO */}
                            <div className="overflow-hidden rounded-[20px] bg-[#064E3B] text-white">
                                <div className="relative h-[155px] overflow-hidden">
                                    <img
                                        src={blogImg}
                                        alt="Dubai travel inspiration"
                                        className="h-full w-full object-cover opacity-60"
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B] to-transparent" />
                                </div>

                                <div className="px-7 pb-8">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-[#B6F2CB]">
                                        <Compass size={25} />
                                    </div>

                                    <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#B6F2CB]">
                                        Your Dubai Travel Companion
                                    </p>

                                    <h2 className="mt-3 text-[26px] font-extrabold leading-tight !text-white">
                                        Explore More Of Dubai.
                                    </h2>

                                    <p className="mt-4 text-[13px] leading-7 text-white/75">
                                        Discover destination guides, hotel tips,
                                        and travel inspiration for your next Dubai visit.
                                    </p>

                                    <Link
                                        to="/blogs"
                                        className="mt-6 inline-flex items-center gap-3 text-sm font-extrabold text-[#B6F2CB] transition hover:gap-4"
                                    >
                                        Explore All Articles
                                        <ArrowRight size={17} />
                                    </Link>
                                </div>
                            </div>

                            {/* LATEST STORIES */}
                            <div className="rounded-[20px] border border-[#E2ECE6] bg-white p-6">
                                <div className="flex items-center justify-between gap-3">
                                    <h2 className="text-[21px] font-extrabold text-[#17324D]">
                                        Latest Stories
                                    </h2>

                                    <Link
                                        to="/blogs"
                                        className="text-xs font-extrabold text-[#168B43] hover:underline"
                                    >
                                        View All
                                    </Link>
                                </div>

                                <div className="mt-5 space-y-5">
                                    {latestOtherBlogs.length > 0 ? (
                                        latestOtherBlogs.map((item) => (
                                            <Link
                                                key={item.id ?? item.slug}
                                                to={`/blogs/${item.slug}`}
                                                className="group flex gap-4 border-b border-[#E8F0EB] pb-5 last:border-b-0 last:pb-0"
                                            >
                                                <div className="h-[80px] w-[90px] shrink-0 overflow-hidden rounded-xl bg-[#E8F0EB]">
                                                    <img
                                                        src={getImage(
                                                            item.thumbnail_image || item.image
                                                        )}
                                                        alt={item.title}
                                                        loading="lazy"
                                                        onError={imageFallback}
                                                        className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                                                    />
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#168B43]">
                                                        {item.category?.name || "Travel Guide"}
                                                    </p>

                                                    <h3 className="mt-1 line-clamp-2 text-[13px] font-extrabold leading-5 text-[#17324D] transition group-hover:text-[#168B43]">
                                                        {item.title}
                                                    </h3>

                                                    {item.created_at && (
                                                        <p className="mt-2 text-[11px] text-[#8492A6]">
                                                            {formatDate(item.created_at, true)}
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                        ))
                                    ) : (
                                        <p className="text-sm leading-7 text-[#718096]">
                                            More travel stories will appear here soon.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* HOTEL BOOKING CARD */}
                            <div className="overflow-hidden rounded-[20px] border border-[#E2ECE6] bg-white">
                                <div className="relative h-[175px] overflow-hidden">
                                    <img
                                        src={HOTEL_IMAGE}
                                        alt="Dubai hotel accommodation"
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                </div>

                                <div className="p-6">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                        <Hotel size={23} />
                                    </div>

                                    <h2 className="mt-4 text-[23px] font-extrabold leading-tight text-[#17324D]">
                                        Planning a Dubai Stay?
                                    </h2>

                                    <p className="mt-3 text-[13px] leading-7 text-[#718096]">
                                        Explore Dubai hotels and discover accommodation
                                        options that suit your travel plans and budget.
                                    </p>

                                    <Link
                                        to="/hotels"
                                        className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-[#168B43] px-5 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                                    >
                                        Explore Hotels
                                        <ArrowRight size={17} />
                                    </Link>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>

                {/* =====================================================
            RELATED ARTICLES
        ===================================================== */}

                {relatedBlogs.length > 0 && (
                    <section className="bg-white py-16 sm:py-24">
                        <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
                            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
                                <div>
                                    <div className="mb-4 flex items-center gap-3">
                                        <span className="h-[2px] w-8 bg-[#168B43]" />

                                        <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                                            Continue Exploring
                                        </p>
                                    </div>

                                    <h2 className="text-[32px] font-extrabold tracking-tight text-[#17324D] sm:text-[44px]">
                                        You May Also{" "}
                                        <span className="text-[#168B43]">
                                            Like.
                                        </span>
                                    </h2>

                                    <p className="mt-4 text-sm text-[#718096]">
                                        Discover more travel guides and Dubai inspiration.
                                    </p>
                                </div>

                                <Link
                                    to="/blogs"
                                    className="inline-flex items-center gap-3 text-sm font-extrabold text-[#168B43] transition hover:gap-4"
                                >
                                    View All Articles
                                    <ArrowRight size={17} />
                                </Link>
                            </div>

                            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                                {relatedBlogs.map((item) => (
                                    <RelatedCard
                                        key={item.id ?? item.slug}
                                        blog={item}
                                    />
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* =====================================================
            FINAL HOTEL CTA
        ===================================================== */}

                <section className="bg-white pb-16 sm:pb-24">
                    <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
                        <div className="relative isolate overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-16 text-center sm:px-12 sm:py-20">
                            <img
                                src={HOTEL_IMAGE}
                                alt=""
                                loading="lazy"
                                className="absolute inset-0 -z-20 h-full w-full object-cover"
                            />

                            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/75" />

                            <div className="mx-auto max-w-[740px]">
                                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/10 text-[#B6F2CB]">
                                    <Hotel size={26} />
                                </div>

                                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                    Discover Your Next Stay
                                </p>

                                <h2 className="mt-4 text-[33px] font-extrabold leading-tight tracking-[-0.04em] text-white sm:text-[49px]">
                                    Inspired By Dubai?
                                    <br />
                                    Find Your Perfect Hotel.
                                </h2>

                                <p className="mx-auto mt-5 max-w-[550px] text-sm leading-8 text-white/75">
                                    Explore hotel options across Dubai and find
                                    accommodation that suits your travel plans.
                                </p>

                                <Link
                                    to="/hotels"
                                    className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                                >
                                    Explore Dubai Hotels
                                    <ArrowRight size={17} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
